import React, { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { startOfDay } from "date-fns";
import { styled } from "@mui/material/styles";

import { withState } from "../../../services/state";

import {
  getMembers as apiGetMembers,
  getAvailabilityRequest as apiGetAvailabilityRequest,
  createAvailabilityRequest as apiCreateAvailabilityRequest,
  updateAvailabilityRequest as apiUpdateAvailabilityRequest,
  addPlayerToAvailabilityRequest as apiAddPlayerToAvailabilityRequest,
  removePlayerFromAvailabilityRequest as apiRemovePlayerFromAvailabilityRequest,
  getTeams as apiGetTeams,
  getEventTypes as apiGetEventTypes,
  getEventLocations as apiGetEventLocations,
  selectLastTeamForAvailabilityRequest as apiSelectLastTeamForAvailabilityRequest,
} from "../../../services/api";

import PageWrapper from "../../components/pageWrapper/PageWrapper";
import Section from "../../components/section/Section";
import DialogModal from "../../components/dialogModal/DialogModal";
import Button from "../../components/button/Button";

import AddAvailabilityRequestForm from "../../forms/addAvailabilityRequestForm/AddAvailabilityRequestForm";
import AvailabilityRequestPlayersTable from "../../tables/availabilityRequestPlayersTable/AvailabilityRequestPlayersTable";
import ToggleAvailabilityRequestPlayersForm from "../../forms/toggleAvailabilityRequestPlayersForm/ToggleAvailabilityRequestPlayersForm";
import SingleLineTextInput from "../../components/singleLineTextInput/SingleLineTextInput";

const FormActions = styled("div")({
  textAlign: "right",
});

const ChoosePlayerButtons = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 10,
  "& > div": {
    margin: "0 5px",
  },
  [theme.breakpoints.down("sm")]: {
    display: "block",
    "& > div": {
      marginBottom: "10px",
    },
  },
}));

const getPlayerType = (currentClub) => {
  if (currentClub.isTreasurer) return "treasurer";
  if (currentClub.isCaptain) return "captain";
  return "player";
};

const event_action = {
  captain:
    "Here you can enter details of your upcoming fixtures and pick your teams. If you hit the Slate logo to go back to the events list, the details will be saved and you or the Club Treasurer can still edit them. Please note that once you CONFIRM an event, the details cannot be changed. Payments from your players will only be collected once an event is confirmed.",
  treasurer:
    "Here you can enter details of your upcoming fixtures and pick your teams. If you hit the Slate logo to go back to the events list, the details will be saved and you or the Team Manager can still edit them. Please note that once you CONFIRM an event, the details cannot be changed. Payments from your players will only be collected once an event is confirmed.",
};

function CreateAvailabilityRequestPage({ classes, dispatch, context }) {
  const navigate = useNavigate();
  const [showAddPlayer, toggleShowAddPlayer] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [members, setMembers] = React.useState([]);
  const [availabilityRequest, setAvailabilityRequest] = React.useState({
    name: "",
    eventDate: startOfDay(new Date()).toISOString(),
    players: [],
    eventTypeId: "",
    teamId: "",
  });
  // const [creatingEvent, setCreatingEvent] = React.useState(true);
  // const [createAvailabilityRequestError, setCreateEventError] = React.useState(true);
  const [addingPlayer, setAddingPlayer] = React.useState(true);
  // const [addingPlayerError, setAddPlayerError] = React.useState(true);
  // const [showDeletePlayer, setDeletePlayer] = React.useState(null);
  const [deletingPlayer, setDeletingPlayer] = React.useState(false);
  // const [deletePlayerError, setDeletePlayerError] = React.useState(true);
  const [updatingEvent, setUpdatingEvent] = React.useState(false);
  const [gettingTeams, setGettingTeams] = React.useState(false);
  const [teams, setTeams] = React.useState([]);
  const [eventLocations, setEventLocations] = React.useState([]);
  const [gettingEventLocations, setGettingEventLocations] =
    React.useState(true);
  const [gettingEventTypes, setGettingEventTypes] = React.useState(false);
  const [eventTypes, setEventTypes] = React.useState([]);
  const [selectingLastTeam, setSelectingLastTeam] = React.useState(false);
  const [showEditEventInfo, setShowEditEventInfo] = React.useState(false);

  const getMembers = useCallback(async () => {
    try {
      if (!context.currentClub.clubId) {
        return false;
      }

      const data = await apiGetMembers(
        context.currentClub.clubId,
        context.currentPlayer.id
      );
      if (data && !data.reason) {
        setMembers(data);
      }
    } catch (e) {
      console.log(e);
    }
  }, [context.currentClub.clubId, context.currentPlayer.id]);

  const getAvailabilityRequest = async () => {
    try {
      const data = await apiGetAvailabilityRequest(availabilityRequest.id);
      if (data && !data.reason) {
        setAvailabilityRequest({
          ...data,
          teamId: data && data.team && data.team.id,
          eventTypeId: data && data.type && data.type.id,
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const storePayload = async (payload) => {
    if (
      !payload.id &&
      payload.name &&
      payload.eventDate &&
      payload.teamId &&
      payload.eventTypeId
    ) {
      payload.id = await apiCreateAvailabilityRequest({
        name: payload.name,
        eventLocation: payload.eventLocation,
        eventDate: payload.eventDate,
        eventStartTime: payload.eventStartTime,
        eventEndTime: payload.eventEndTime,
        teamId: payload.teamId,
        eventTypeId: payload.eventTypeId,
      });
    }

    setAvailabilityRequest(payload);
  };

  const updateAvailabilityRequest = async () => {
    try {
      setUpdatingEvent(true);
      await apiUpdateAvailabilityRequest(
        availabilityRequest.id,
        availabilityRequest
      );
      setUpdatingEvent(false);
      // Navigate to the newly created availability request page
      navigate(`/availability-requests/${availabilityRequest.id}`);
    } catch (e) {
      console.log(e);
      setUpdatingEvent(false);
    }
  };

  const getTeams = useCallback(async () => {
    if (
      !context.currentClub ||
      !context.currentClub.clubId ||
      !context.currentPlayer ||
      !context.currentPlayer.id
    ) {
      return false;
    }

    try {
      setGettingTeams(true);
      const data = await apiGetTeams(
        context.currentClub.clubId,
        context.currentPlayer.id
      );
      if (data && !data.reason) {
        let teams = data;
        if (context.currentPlayer.isCaptain) {
          teams = data.filter((team) => team.isTeamCaptain);
        }
        setTeams(teams);
      }
      setGettingTeams(false);
    } catch (e) {
      console.log(e);
      setGettingTeams(false);
    }
  }, [context.currentClub, context.currentPlayer]);

  const getEventTypes = useCallback(async () => {
    if (
      !context.currentClub ||
      !context.currentClub.clubId ||
      !context.currentPlayer ||
      !context.currentPlayer.id
    ) {
      return false;
    }

    try {
      setGettingEventTypes(true);
      const data = await apiGetEventTypes(
        context.currentClub.clubId,
        context.currentPlayer.id
      );
      if (data && !data.reason) {
        setEventTypes(data);
      }
      setGettingEventTypes(false);
    } catch (e) {
      console.log(e);
      setGettingEventTypes(false);
    }
  }, [context.currentClub, context.currentPlayer]);

  const getEventLocations = useCallback(async () => {
    try {
      setGettingEventLocations(true);
      const data = await apiGetEventLocations(
        context.currentClub.clubId,
        context.currentPlayer.id
      );
      if (data && !data.reason) {
        setEventLocations(data);
      }
      setGettingEventLocations(false);
    } catch (e) {
      console.log(e);
      setGettingEventLocations(false);
    }
  }, [context.currentClub, context.currentPlayer]);

  const addPlayerToAvailabilityRequest = async (member) => {
    try {
      setAddingPlayer(member.playerId);
      await apiAddPlayerToAvailabilityRequest(
        availabilityRequest.id,
        member.playerId
      );
      setAddingPlayer(false);
      getAvailabilityRequest();
    } catch (e) {
      setAddingPlayer(false);
      // setAddPlayerError(e.message);
    }
  };

  const removePlayerFromAvailabilityRequest = async (eventPlayer) => {
    try {
      setDeletingPlayer(eventPlayer.player.id);
      await apiRemovePlayerFromAvailabilityRequest(
        availabilityRequest.id,
        eventPlayer.player.id
      );
      // setDeletePlayer(null);
      setDeletingPlayer(null);
      getAvailabilityRequest();
    } catch (e) {
      setDeletingPlayer(null);
      // setDeletePlayerError(e.message);
    }
  };

  const selectLastTeam = async () => {
    setSelectingLastTeam(true);
    await apiSelectLastTeamForAvailabilityRequest(availabilityRequest.id);
    await getAvailabilityRequest();
    setSelectingLastTeam(false);
  };

  const isPlayerSelected = (member) => {
    return availabilityRequest.players.find(
      (eventPlayer) => eventPlayer.player.id === member.playerId
    );
  };

  const reorderClubPlayers = () => {
    setMembers([]);
    setSearchTerm("");

    setTimeout(() => {
      setMembers(
        members.sort((a, b) => {
          let aSelected = isPlayerSelected(a);
          let bSelected = isPlayerSelected(b);

          if (aSelected && !bSelected) {
            return -1;
          } else if (bSelected && !aSelected) {
            return 1;
          } else {
            let aName = `${a.firstName} ${a.lastName}`;
            let bName = `${b.firstName} ${b.lastName}`;

            if (aName !== bName) {
              return bName > aName ? -1 : 1;
            } else {
              return b.id > a.id ? -1 : 1;
            }
          }
        })
      );
    }, 10);
  };

  useEffect(() => {
    getMembers();
    getTeams();
    getEventTypes();
    getEventLocations();
  }, [getMembers, getTeams, getEventTypes, getEventLocations]);

  return (
    <PageWrapper
      showHeader={true}
      showSidebar={true}
      title="Create availability request"
      showLoading={gettingTeams || gettingEventLocations || gettingEventTypes}
      onInfoClick={
        event_action[getPlayerType(context.currentClub)]
          ? () => setShowEditEventInfo(true)
          : null
      }
    >
      <Section>
        <AddAvailabilityRequestForm
          payload={availabilityRequest}
          storePayload={(payload) => storePayload(payload)}
          teams={teams}
          eventTypes={eventTypes}
          eventLocations={eventLocations}
          setEventLocations={setEventLocations}
        />
        {(context.currentClub.isTreasurer || context.currentClub.isCaptain) && (
          <FormActions>
            <Button
              title="Save"
              onClick={() => {
                updateAvailabilityRequest();
              }}
              submitting={updatingEvent}
              disabled={!availabilityRequest.id}
            />
          </FormActions>
        )}
      </Section>
      <Section>
        {(context.currentClub.isTreasurer || context.currentClub.isCaptain) &&
          availabilityRequest.id && (
            <AvailabilityRequestPlayersTable
              players={availabilityRequest.players}
              additionalButtons={[
                {
                  title: "Edit players",
                  onPress: () => toggleShowAddPlayer(true),
                },
              ]}
              isTreasurerOrCaptain={
                context.currentClub.isTreasurer || context.currentClub.isCaptain
              }
              myPlayer={context.currentPlayer}
            />
          )}
        {!availabilityRequest.id && (
          <div>
            <div>Select an event type and a team</div>
            <div>
              You must select an event type and a team before editing players.
            </div>
          </div>
        )}
      </Section>

      {showAddPlayer && (
        <DialogModal
          title="Choose event players"
          onClose={() => toggleShowAddPlayer(false)}
          cancelText="Close"
        >
          <ChoosePlayerButtons>
            <SingleLineTextInput
              placeholder="Search"
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
            />
            <div>
              <Button
                title="REORDER"
                onClick={() => reorderClubPlayers()}
                style={{ marginRight: 10 }}
              />
              <Button
                title="LAST TEAM"
                onClick={() => selectLastTeam()}
                submitting={selectingLastTeam}
              />
            </div>
          </ChoosePlayerButtons>
          <ToggleAvailabilityRequestPlayersForm
            members={members.filter(
              (member) =>
                `${member.firstName} ${member.lastName}`
                  .toLowerCase()
                  .indexOf(searchTerm.toLowerCase()) !== -1
            )}
            players={availabilityRequest.players}
            availabilityRequestPlayers={availabilityRequest.players || []}
            onAddPlayer={(membership) => {
              addPlayerToAvailabilityRequest(membership);
            }}
            addingPlayer={addingPlayer}
            onRemovePlayer={(eventPlayer) => {
              removePlayerFromAvailabilityRequest(eventPlayer);
            }}
            deletingPlayer={deletingPlayer}
            onClose={() => toggleShowAddPlayer(false)}
          />
        </DialogModal>
      )}

      {showEditEventInfo && (
        <DialogModal
          title="Your events"
          onClose={() => setShowEditEventInfo(null)}
          cancelText="Close"
        >
          <div>{event_action[getPlayerType(context.currentClub)]}</div>
        </DialogModal>
      )}
    </PageWrapper>
  );
}

export default withState(CreateAvailabilityRequestPage);
